/**
 * Announcement-reply "Re:" label — shared by mobile (U8) and web (U9) so the
 * two surfaces format the label identically and never diverge.
 *
 * The label resolves to the origin broadcast group's live `name` at render
 * time (a later rename shows through), truncated for narrow chrome. A
 * deleted / soft-deleted origin — surfaced as a null/absent
 * `announcementOriginGroup` — falls back to the generic "Re: Announcement".
 */

/** The origin-group slice both surfaces read off a CONTACT_INQUIRY reply group. */
export interface AnnouncementOriginGroupRef {
  id: string
  name: string
}

/** Generic fallback shown when the origin announcement is deleted / unavailable. */
export const ANNOUNCEMENT_REPLY_FALLBACK_LABEL = 'Re: Announcement'

/** Default max length of the announcement name portion before ellipsizing. */
export const ANNOUNCEMENT_REPLY_NAME_MAX_LENGTH = 40

/**
 * Format the "Re: <announcement>" label for an announcement reply thread.
 *
 * @param origin - The reply group's `announcementOriginGroup` (null/undefined
 *   when the origin was deleted / soft-deleted).
 * @param maxLength - Max length of the announcement name before truncation
 *   (default 40 chars); the name is ellipsized with a trailing "…".
 */
export function formatAnnouncementReplyLabel(
  origin: AnnouncementOriginGroupRef | null | undefined,
  maxLength: number = ANNOUNCEMENT_REPLY_NAME_MAX_LENGTH
): string {
  const name = origin?.name?.trim()
  if (!name) return ANNOUNCEMENT_REPLY_FALLBACK_LABEL

  const truncated = name.length > maxLength ? `${name.slice(0, maxLength).trimEnd()}…` : name
  return `Re: ${truncated}`
}
